import type {
  OnboardingStateMap,
  OnboardingTourDefinition,
  OnboardingTourProgress,
} from './types'

export const ONBOARDING_STORAGE_KEY = 'monorga.onboarding.v1'

export function readOnboardingStateFromStorage(): OnboardingStateMap {
  if (import.meta.server) {
    return {}
  }
  try {
    const raw = window.localStorage.getItem(ONBOARDING_STORAGE_KEY)
    if (raw === null || raw.trim() === '') {
      return {}
    }
    const parsed = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) {
      return {}
    }
    return parsed as OnboardingStateMap
  } catch {
    return {}
  }
}

export function writeOnboardingStateToStorage(state: OnboardingStateMap): void {
  if (import.meta.server) {
    return
  }
  try {
    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Espace localStorage saturé ou inaccessible : on garde l’état en mémoire.
  }
}

export function shouldSkipTourByStoredState(
  tour: OnboardingTourDefinition,
  existing: OnboardingTourProgress | undefined
): boolean {
  if (existing === undefined) {
    return false
  }
  if (existing.version !== tour.version) {
    return false
  }
  return existing.status === 'completed'
}

function timestampOf(progress: OnboardingTourProgress | undefined): number {
  if (progress === undefined) {
    return 0
  }
  const parsed = Date.parse(progress.updatedAt)
  if (Number.isNaN(parsed)) {
    return 0
  }
  return parsed
}

export function mergeOnboardingStateByNewest(
  localState: OnboardingStateMap,
  serverState: OnboardingStateMap
): OnboardingStateMap {
  const merged: OnboardingStateMap = { ...serverState }
  for (const [tourId, localProgress] of Object.entries(localState)) {
    const serverProgress = serverState[tourId]
    if (isCompletedProgress(localProgress) && isDowngradeOfSameVersion(localProgress, serverProgress)) {
      merged[tourId] = localProgress
      continue
    }
    if (isCompletedProgress(serverProgress) && isDowngradeOfSameVersion(serverProgress, localProgress)) {
      continue
    }
    if (timestampOf(localProgress) >= timestampOf(serverProgress)) {
      merged[tourId] = localProgress
    }
  }
  return merged
}

function isCompletedProgress(progress: OnboardingTourProgress | undefined): boolean {
  return progress?.status === 'completed'
}

function isDowngradeOfSameVersion(
  completed: OnboardingTourProgress | undefined,
  candidate: OnboardingTourProgress | undefined
): boolean {
  if (completed === undefined || candidate === undefined) {
    return false
  }
  return completed.version === candidate.version && candidate.status !== 'completed'
}
